import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../api/api';
import { Card, Input, Label, Button, Icon } from '../../components/ui';

function formatMoney(v) {
  const n = Number(v || 0);
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

function toInputDate(d) {
  const dt = new Date(d);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export default function AccountingAR() {
  const [from, setFrom] = useState(() => toInputDate(new Date(new Date().getFullYear(), 0, 1)));
  const [to, setTo] = useState(() => toInputDate(new Date()));

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  const [q, setQ] = useState('');

  const [selectedClientId, setSelectedClientId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailLines, setDetailLines] = useState([]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return items;
    return items.filter((x) => {
      const id = String(x?._id || '').toLowerCase();
      const name = String(x?.clientName || '').toLowerCase();
      const mf = String(x?.clientTaxId || '').toLowerCase();
      return id.includes(qq) || name.includes(qq) || mf.includes(qq);
    });
  }, [items, q]);

  const totals = useMemo(() => {
    const debit = filtered.reduce((s, x) => s + Number(x.debit || 0), 0);
    const credit = filtered.reduce((s, x) => s + Number(x.credit || 0), 0);
    const balance = debit - credit;
    return { debit, credit, balance };
  }, [filtered]);

  const loadSummary = async () => {
    try {
      setLoading(true);
      setSelectedClientId(null);
      setDetailLines([]);
      const res = await api.get('/accounting/ar/summary', { params: { from, to } });
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadClientLines = async (clientId) => {
    try {
      setSelectedClientId(clientId);
      setDetailLines([]);
      setDetailLoading(true);
      const res = await api.get(`/accounting/ar/${clientId}/lines`, { params: { from, to } });
      setDetailLines(res.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          <Icon name="IconHandHoldingDollar" className="text-indigo-600 mr-3" />
          Postes clients (AR)
        </h1>
        <p className="text-sm text-gray-600 mt-1">Calcul basé sur les comptes 411* (débit - crédit) sur la période.</p>
      </div>

      {/* Filtres */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs text-gray-500">Du</Label>
            <Input
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              type="date"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Au</Label>
            <Input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              type="date"
            />
          </div>

          <div className="md:col-span-2">
            <Label className="text-xs text-gray-500">Recherche (Nom / MF / ID)</Label>
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Société / Matricule fiscal / ObjectId…"
            />
          </div>

          <div className="flex items-end">
            <Button
              type="button"
              onClick={loadSummary}
              disabled={loading}
              className="w-full"
            >
              {loading ? 'Chargement…' : 'Charger'}
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Summary */}
        <div className="lg:col-span-2">
          <Card>
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm text-gray-600">
                {loading ? '—' : `${filtered.length} clients`} · Total Débit {formatMoney(totals.debit)} · Total Crédit{' '}
                {formatMoney(totals.credit)} · Solde {formatMoney(totals.balance)}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Client</th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Débit</th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Crédit</th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Solde</th>
                    <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>

                <tbody className="divide-y">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-8 text-center text-gray-500">
                        Aucun résultat. Clique "Charger".
                      </td>
                    </tr>
                  ) : (
                    filtered.map((x) => {
                      const id = x?._id;
                      const balance = Number(x?.balance || 0);
                      const active = selectedClientId && String(selectedClientId) === String(id);

                      return (
                        <tr key={id} className={active ? 'bg-indigo-50' : 'hover:bg-gray-50'}>
                          <td className="px-3 py-2">
                            <div className="text-sm text-gray-900 font-medium">{x.clientName || 'Client (inconnu)'}</div>
                            <div className="text-[11px] text-gray-500 break-words">
                              {x.clientTaxId ? `MF: ${x.clientTaxId} — ` : ''}
                              {String(id)}
                            </div>
                          </td>

                          <td className="px-3 py-2 text-sm text-right text-gray-700">{formatMoney(x.debit)}</td>
                          <td className="px-3 py-2 text-sm text-right text-gray-700">{formatMoney(x.credit)}</td>
                          <td
                            className={`px-3 py-2 text-sm text-right font-semibold ${
                              balance >= 0 ? 'text-amber-700' : 'text-green-700'
                            }`}
                          >
                            {formatMoney(balance)}
                          </td>

                          <td className="px-3 py-2 text-right">
                            <Button
                              type="button"
                              onClick={() => loadClientLines(id)}
                              size="sm"
                            >
                              Détails
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Detail */}
        <Card>
          <div className="font-semibold text-gray-800 mb-2">Détails client</div>

          {!selectedClientId ? (
            <div className="text-sm text-gray-500">Sélectionne un client dans la liste.</div>
          ) : detailLoading ? (
            <div className="text-sm text-gray-600">Chargement…</div>
          ) : (
            <>
              <div className="text-xs text-gray-600 break-words mb-3">ClientId: {String(selectedClientId)}</div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-2 text-left text-xs text-gray-500 uppercase">Date</th>
                      <th className="px-2 py-2 text-left text-xs text-gray-500 uppercase">Pièce</th>
                      <th className="px-2 py-2 text-left text-xs text-gray-500 uppercase">Compte</th>
                      <th className="px-2 py-2 text-right text-xs text-gray-500 uppercase">Débit</th>
                      <th className="px-2 py-2 text-right text-xs text-gray-500 uppercase">Crédit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {detailLines.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-2 py-6 text-center text-gray-500">
                          Aucune ligne.
                        </td>
                      </tr>
                    ) : (
                      detailLines.map((l) => (
                        <tr key={l._id || `${l.entryId}-${l.lineNo}`}>
                          <td className="px-2 py-2 text-xs text-gray-700">
                            {l.entryDate ? new Date(l.entryDate).toLocaleDateString('fr-FR') : '-'}
                          </td>
                          <td className="px-2 py-2 text-xs text-gray-700">
                            {l.journalCode}-{l.entryNumber}
                            <div className="text-[11px] text-gray-500">{l.sourceRef || l.entryLabel || '-'}</div>
                          </td>
                          <td className="px-2 py-2 text-xs text-gray-700">{l.accountCode}</td>
                          <td className="px-2 py-2 text-xs text-right text-gray-700">{formatMoney(l.debit)}</td>
                          <td className="px-2 py-2 text-xs text-right text-gray-700">{formatMoney(l.credit)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
